/**
 * טופס הבקשה — TASKS 4.3–4.4 · PRD 7.2 (שלוש שכבות הטלפון), 12.2 (CAPTCHA).
 *
 * ⛔ ⛔ **אין מחיר בשום מקום בעמוד הזה** (6.5).
 *
 * ⚠️ **16.08.2026, לבקשת אריאנה — הוסר צעד האישור החוזר בלחיצה שנייה** (היה
 *    כאן "ההצעה תישלח למספר X — נכון?" עם שני כפתורים). זו הייתה המחשה
 *    ל-PRD 7.2 שכבה 1 ("הצגה חוזרת לפני שליחה"), אבל יצרה קליק מיותר בכל
 *    שליחה. **הפתרון:** אותה תזכורת מוצגת עכשיו כטקסט מידע קבוע מעל כפתור
 *    השליחה (⛔ לא שער חוסם) — הלקוח עדיין רואה לאן ההצעה נשלחת לפני שהוא
 *    לוחץ, אבל לחיצה אחת שולחת.
 */
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// ⚠️ תת-נתיב, ⛔ לא הבארל `@noam/shared` — ראו ההערה ב-CartPage.tsx
import type { NormalizePhoneResult } from '@noam/shared/phone';
import { useCart } from '../cart/CartContext.js';
import { PhoneField } from '../request/PhoneField.js';
import { Captcha } from '../request/Captcha.js';
import { isPublicApiError, submitQuoteRequest } from '../publicApi/quotesApi.js';
import { FieldError } from '../components/FieldError.js';

export function RequestPage() {
  const cart = useCart();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [phoneResult, setPhoneResult] = useState<NormalizePhoneResult | null>(null);
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [notes, setNotes] = useState('');
  // ⭐ 16.08.2026 — אוחדו לתיבת סימון אחת (הייתה שתי תיבות נפרדות). מבחינת
  // השרת ומדיניות Meta אלה עדיין שתי הסכמות נפרדות (`whatsappConsent` +
  // `termsConsent`) — כאן הן רק מוצגות וממוסמנות יחד, ולכן מקבלות תמיד את
  // אותו ערך.
  const [consent, setConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ⭐ אין מה לבקש הצעה בלי עגלה — ⛔ אבל לא מפריע ברגע שאחרי שליחה מוצלחת
  //    (ThankYouPage מרוקן את העגלה **אחרי** הניווט, ראו שם).
  useEffect(() => {
    if (cart.items.length === 0) navigate('/cart', { replace: true });
  }, [cart.items.length, navigate]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isFormValid =
    fullName.trim().length >= 2 &&
    phoneResult?.ok === true &&
    emailValid &&
    consent &&
    captchaToken.length > 0;

  async function doSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitQuoteRequest({
        fullName,
        phone: phoneResult?.ok ? phoneResult.e164 : '',
        email,
        company: company.trim() || undefined,
        notes: notes.trim() || undefined,
        whatsappConsent: consent,
        termsConsent: consent,
        captchaToken,
        items: cart.items,
      });
      navigate('/request/thank-you', { state: result, replace: true });
    } catch (err) {
      setError(isPublicApiError(err) ? err.message : 'אירעה שגיאה בשליחת הבקשה. נסו שוב.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAttempted(true);
    if (!isFormValid) return;
    void doSubmit();
  }

  return (
    // ⭐ DESIGN.md 5.6 — "עמודה אחת max-w-lg" (⛔ לא max-w-2xl).
    <div className="mx-auto max-w-lg px-6 py-16">
      <h1 className="text-3xl font-bold">פרטי הבקשה</h1>
      <p className="text-muted mt-2">
        השאירו פרטים ונשלח אליכם הצעת מחיר מלאה בדוא"ל, בהתאם למידות שבחרתם בעגלה.
      </p>
      <p className="mt-1">
        <Link to="/cart" className="text-link underline">
          חזרה לעגלה ({cart.items.length} פריטים)
        </Link>
      </p>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
        <div>
          <label htmlFor="request-name" className="block text-sm font-semibold">
            שם מלא*
          </label>
          <input
            id="request-name"
            name="fullName"
            type="text"
            required
            aria-required="true"
            aria-invalid={attempted && fullName.trim().length < 2 ? 'true' : undefined}
            aria-describedby={
              attempted && fullName.trim().length < 2 ? 'request-name-error' : undefined
            }
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            className="mt-1 min-h-11 w-full rounded-md border border-line px-3 py-2"
          />
          {attempted && fullName.trim().length < 2 && (
            <FieldError id="request-name-error">יש להזין שם מלא.</FieldError>
          )}
        </div>

        <PhoneField label="טלפון" onResult={setPhoneResult} />

        <div>
          <label htmlFor="request-email" className="block text-sm font-semibold">
            דוא"ל*
          </label>
          <input
            id="request-email"
            name="email"
            type="email"
            dir="ltr"
            required
            aria-required="true"
            aria-invalid={attempted && !emailValid ? 'true' : undefined}
            aria-describedby={attempted && !emailValid ? 'request-email-error' : undefined}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="ltr-inline mt-1 min-h-11 w-full max-w-80 rounded-md border border-line px-3 py-2"
          />
          {attempted && !emailValid && (
            <FieldError id="request-email-error">
              יש להזין כתובת דוא"ל תקינה - ההצעה תישלח אליה.
            </FieldError>
          )}
        </div>

        <div>
          <label htmlFor="request-company" className="block text-sm font-semibold">
            חברה
          </label>
          <input
            id="request-company"
            name="company"
            type="text"
            value={company}
            onChange={(event) => setCompany(event.target.value)}
            className="mt-1 min-h-11 w-full rounded-md border border-line px-3 py-2"
          />
        </div>

        <div>
          <label htmlFor="request-notes" className="block text-sm font-semibold">
            הערות
          </label>
          <textarea
            id="request-notes"
            name="notes"
            rows={4}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="mt-1 w-full rounded-md border border-line px-3 py-2"
          />
        </div>

        <div>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              name="consent"
              required
              aria-required="true"
              aria-invalid={attempted && !consent ? 'true' : undefined}
              aria-describedby={attempted && !consent ? 'request-consent-error' : undefined}
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              className="mt-1"
            />
            <span className="text-sm">
              אני מאשר/ת קבלת הצעת המחיר ופרטי יצירת קשר נוספים בוואטסאפ ובדוא"ל, וקראתי ומקבל/ת את{' '}
              <Link to="/terms" className="text-link underline">
                תנאי השימוש
              </Link>{' '}
              ואת{' '}
              <Link to="/privacy" className="text-link underline">
                מדיניות הפרטיות
              </Link>
              .*
            </span>
          </label>
          {attempted && !consent && (
            <FieldError id="request-consent-error">
              יש לאשר את התנאים כדי לשלוח את הבקשה.
            </FieldError>
          )}
        </div>

        <Captcha onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} />

        {/* ⭐ תזכורת קבועה, ⛔ לא שער חוסם — ראו ההערה בראש הקובץ. ⛔ בכוונה
            בלי role="status": הפסקה משתנה בכל הקשה בשדה הדוא"ל, ו-live
            region הייתה מכריזה על כל תו בנפרד לקורא מסך. */}
        {phoneResult?.ok && (
          <p className="text-muted text-sm">
            ההצעה תישלח למספר{' '}
            <span className="ltr-inline font-semibold">{phoneResult.display}</span> ולכתובת{' '}
            <span className="font-semibold">{email}</span>.
          </p>
        )}

        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}

        <button type="submit" className="btn btn-cta" disabled={submitting}>
          {submitting ? 'שולח…' : 'שליחת הבקשה'}
        </button>
      </form>
    </div>
  );
}
